import { useState, useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getReportBySlug } from "../../data/reportsData";
import { PasswordGate } from "../../components/reports/PasswordGate";
import { WattsReport } from "../../components/reports/WattsReport";
import { SunyReport } from "../../components/reports/SunyReport";
import "../../styles/reportStyles.css";

// ═══════════════════════════════════════════════════════════════════
// REPORT VIEWER - Handles routing and password protection
// ═══════════════════════════════════════════════════════════════════

export default function ReportViewer() {
  const { slug } = useParams();
  const [isUnlocked, setIsUnlocked] = useState(false);

  // Get report data immediately, not in useEffect
  const reportData = getReportBySlug(slug);

  useEffect(() => {
    // Check if this report was previously unlocked in this session
    const unlockedReports = sessionStorage.getItem("unlockedReports");
    if (unlockedReports) {
      const unlocked = JSON.parse(unlockedReports);
      if (unlocked.includes(slug)) {
        setIsUnlocked(true);
      }
    }
  }, [slug]);

  // Dynamic meta tags are now handled by Helmet in the render method

  const handleUnlock = () => {
    setIsUnlocked(true);

    // Store in session storage to maintain unlock during the session
    const unlockedReports = sessionStorage.getItem("unlockedReports");
    const unlocked = unlockedReports ? JSON.parse(unlockedReports) : [];
    if (!unlocked.includes(slug)) {
      unlocked.push(slug);
      sessionStorage.setItem("unlockedReports", JSON.stringify(unlocked));
    }
  };

  // If report doesn't exist, redirect to homepage
  if (!reportData) {
    return <Navigate to="/" replace />;
  }

  // Create meta tag values
  const pageTitle = `Budmen Edu Report: ${reportData.program.name}`;
  const pageDescription = reportData.program.tagline || `${reportData.program.name} - A Budmen Education Program Report`;
  const pageUrl = `https://budmen.services/reports/${slug}`;
  const ogImage = "https://budmen.services/budmen-fabrication-og.png";

  // If not unlocked, show password gate
  if (!isUnlocked) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:image" content={ogImage} />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:type" content="article" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={pageTitle} />
          <meta name="twitter:description" content={pageDescription} />
          <meta name="twitter:image" content={ogImage} />
        </Helmet>
        <PasswordGate
          onUnlock={handleUnlock}
          password={reportData.password}
          reportName={reportData.program.name}
        />
      </>
    );
  }

  // Render the appropriate report component based on the type
  const renderReport = () => {
    // You can determine which template to use based on report properties
    // For now, we'll check if it has cityProfiles (Watts) or dayByDay (SUNY)
    if (reportData.cityProfiles) {
      return <WattsReport data={reportData} />;
    } else if (reportData.dayByDay) {
      return <SunyReport data={reportData} />;
    } else {
      // Default to Watts template or create a generic template
      return <WattsReport data={reportData} />;
    }
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      {renderReport()}
    </>
  );
}